import { z } from 'zod';
import { wireUnion } from '@/lib/api/parse-response';
import type {
  BulkDeleteTaxonomyListsResult,
  TaxonomyListChangeLogEntry,
  TaxonomyListColumn,
  TaxonomyListDetail,
  TaxonomyListImpact,
  TaxonomyListImpactEntry,
  TaxonomyListIndexEntry,
  TaxonomyListSummary,
  TaxonomyListVersion,
  TaxonomyStandardListEntry,
} from '../model/types';

// A missing name reads as '', as withValueAndCode does, rather than failing every list in the response.
const columnSchema: z.ZodType<TaxonomyListColumn> = z.looseObject({
  id: z.string(),
  name: z
    .string()
    .nullish()
    .transform((name) => name ?? ''),
});

export const taxonomyListVersionSchema: z.ZodType<TaxonomyListVersion> = z.looseObject({
  id: z.string(),
  listId: z.string(),
  versionNumber: z.number(),
  parentVersionId: z.string().nullable(),
  status: wireUnion<TaxonomyListVersion['status']>(),
  columns: z.array(columnSchema),
  rows: z.array(z.looseObject({ id: z.string(), cells: z.record(z.string(), z.string()) })),
  description: z.string().nullable(),
  createdBy: z.string().nullable(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  revision: z.number(),
});

const summaryShape = {
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  scope: wireUnion<TaxonomyListSummary['scope']>(),
  currentVersionId: z.string().nullable(),
  version: z.number(),
  currentVersionNumber: z.number().nullable(),
  status: wireUnion<NonNullable<TaxonomyListSummary['status']>>().nullable(),
  columnCount: z.number(),
  rowCount: z.number(),
  columns: z.array(columnSchema),
  boundColumns: z.array(columnSchema),
  libraryKey: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
};

export const taxonomyListSummaryListSchema: z.ZodType<TaxonomyListSummary[]> = z.array(
  z.looseObject(summaryShape),
);

export const taxonomyListIndexSchema: z.ZodType<TaxonomyListIndexEntry[]> = z.array(
  z.looseObject({ ...summaryShape, usageCount: z.number() }),
);

export const taxonomyListDetailSchema: z.ZodType<TaxonomyListDetail> = z.looseObject({
  ...summaryShape,
  versions: z.array(taxonomyListVersionSchema),
});

export const taxonomyListUsageSchema = z.looseObject({ count: z.number() });

export const taxonomyListChangeLogSchema: z.ZodType<TaxonomyListChangeLogEntry[]> = z.array(
  z.looseObject({
    id: z.string(),
    listId: z.string(),
    versionId: z.string().nullable(),
    actorId: z.string(),
    action: wireUnion<TaxonomyListChangeLogEntry['action']>(),
    reason: z.string(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.string(),
  }),
);

const impactEntrySchema: z.ZodType<TaxonomyListImpactEntry> = z.looseObject({
  taxonomyId: z.string(),
  taxonomyName: z.string(),
  fieldNames: z.array(z.string()),
  columnIds: z.array(z.string()),
  follows: z.boolean(),
  pinnedVersionId: z.string().nullable(),
});

export const taxonomyListImpactSchema: z.ZodType<TaxonomyListImpact> = z.looseObject({
  followingCount: z.number(),
  pinnedCount: z.number(),
  schemas: z.array(impactEntrySchema),
});

export const blockingSchemasSchema = z.looseObject({ data: z.array(impactEntrySchema) });

export const standardListCatalogSchema: z.ZodType<TaxonomyStandardListEntry[]> = z.array(
  z.looseObject({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    category: z.string(),
    columns: z.array(z.string()),
    rowCount: z.number(),
    alreadyAdded: z.boolean(),
  }),
);

export const bulkDeleteTaxonomyListsSchema: z.ZodType<BulkDeleteTaxonomyListsResult> =
  z.looseObject({
    deletedIds: z.array(z.string()),
    blocked: z.array(
      z.looseObject({ id: z.string(), name: z.string(), schemaNames: z.array(z.string()) }),
    ),
  });
